import i18n, { currentIntlLocale } from "../../lib/i18n";
import type { Match, MatchPlayerStat, Player, SessionResponse } from "../../types";

export type StatRow = {
  player: Player;
  present: number;
  late: number;
  absent: number;
  noResponse: number;
  responseTotal: number;
  presenceRate: number;
  goals: number;
  assists: number;
  starts: number;
  yellowCards: number;
  redCards: number;
  score: number;
  /** Partite giocate: con risultato, nelle giornate con presenza, piu' quelle con statistica compilata. */
  matchesPlayed: number;
  matchRows: MatchPlayerStat[];
};

export type SquadScorePeriod = "month" | "month_prev" | "all" | "custom";

function tStats(key: string, fallback: string): string {
  const value = i18n.t(`stats:${key}`, { defaultValue: fallback });
  return typeof value === "string" ? value : fallback;
}

export function squadScorePeriodLabel(period: SquadScorePeriod): string {
  const fallbacks: Record<SquadScorePeriod, string> = {
    month: "Mese corrente",
    month_prev: "Mese scorso",
    all: "Tutto il periodo",
    custom: "Personalizzato",
  };
  return tStats(`period.${period}`, fallbacks[period]);
}

export const SQUAD_SCORE_PERIODS: SquadScorePeriod[] = [
  "month",
  "month_prev",
  "all",
  "custom",
];

export function squadScorePeriodBounds(
  period: SquadScorePeriod,
  customFrom?: string,
  customTo?: string,
  nowMs?: number,
): { fromMs: number | null; toMs: number | null; label: string } {
  // nowMs: fine stagione quando si guarda una stagione chiusa.
  const now = new Date(nowMs ?? Date.now());
  const locale = currentIntlLocale();
  if (period === "all") return { fromMs: null, toMs: null, label: squadScorePeriodLabel("all") };
  if (period === "month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { fromMs: from.getTime(), toMs: null, label: now.toLocaleDateString(locale, { month: "long", year: "numeric" }) };
  }
  if (period === "month_prev") {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return { fromMs: from.getTime(), toMs: to.getTime(), label: from.toLocaleDateString(locale, { month: "long", year: "numeric" }) };
  }
  // custom
  const fromMs = customFrom ? new Date(customFrom).getTime() : null;
  const toMs = customTo ? new Date(customTo + "T23:59:59").getTime() : null;
  const label = `${customFrom || "—"} → ${customTo || "—"}`;
  return { fromMs, toMs, label };
}

export type RangeKey = "all" | "7d" | "30d" | "month" | "month_prev";

export function rangeLabel(range: RangeKey): string {
  const fallbacks: Record<RangeKey, string> = {
    all: "Tutto",
    "7d": "Ultimi 7 giorni",
    "30d": "Ultimi 30 giorni",
    month: "Mese corrente",
    month_prev: "Mese scorso",
  };
  return tStats(`range.${range}`, fallbacks[range]);
}

export const RANGE_KEYS: RangeKey[] = ["all", "7d", "30d", "month", "month_prev"];

export function rangeBounds(range: RangeKey, nowMs?: number): { from: number | null; to: number | null } {
  const now = new Date(nowMs ?? Date.now());
  if (range === "all") return { from: null, to: null };
  if (range === "7d") {
    const from = new Date(now);
    from.setDate(now.getDate() - 7);
    return { from: from.getTime(), to: now.getTime() };
  }
  if (range === "30d") {
    const from = new Date(now);
    from.setDate(now.getDate() - 30);
    return { from: from.getTime(), to: now.getTime() };
  }
  if (range === "month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: from.getTime(), to: now.getTime() };
  }
  // month_prev
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  return { from: from.getTime(), to: to.getTime() };
}

/**
 * Partite giocate per giocatore. Le statistiche partita vengono compilate
 * spesso solo per chi segna o fa assist, quindi contarle sottostima le
 * presenze in campo. Si contano le partite con risultato delle giornate a cui
 * il giocatore ha risposto presente o in ritardo, piu' le partite in cui ha
 * comunque una riga di statistica.
 */
export function matchesPlayedByPlayer(
  matches: Match[],
  responses: SessionResponse[],
  matchStats: MatchPlayerStat[],
): Map<string, number> {
  const playedMatches = matches.filter(
    (m) => m.counts_for_stats !== false && m.our_score != null && m.opponent_score != null,
  );
  const presentSessions = new Map<string, Set<string>>();
  for (const r of responses) {
    if (r.status !== "present" && r.status !== "late") continue;
    const set = presentSessions.get(r.player_id) || new Set<string>();
    set.add(r.session_id);
    presentSessions.set(r.player_id, set);
  }
  const counted = new Map<string, Set<string>>();
  const add = (playerId: string, matchId: string) => {
    const set = counted.get(playerId) || new Set<string>();
    set.add(matchId);
    counted.set(playerId, set);
  };
  for (const [playerId, sessions] of presentSessions) {
    for (const m of playedMatches) if (sessions.has(m.session_id)) add(playerId, m.id);
  }
  const playedIds = new Set(playedMatches.map((m) => m.id));
  for (const s of matchStats) if (playedIds.has(s.match_id)) add(s.player_id, s.match_id);
  const out = new Map<string, number>();
  for (const [playerId, set] of counted) out.set(playerId, set.size);
  return out;
}
